import { Button, Label, LinkButton, useToast } from "@themis/ui";
import React from "react";
import {
  generatePath,
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";

import { ModifyAccountRequest } from "@/api";
import {
  useAccount,
  useCreateAccount,
  useUpdateAccount,
} from "@/api/queries/accounts";
import { useContacts } from "@/api/queries/contacts";
import { useAccountContracts } from "@/api/queries/contracts";
import { PageContent } from "@/components/Layout/PageContent";

import { ACCOUNT_ROUTES } from "..";
import {
  AccountInfoForm,
  AccountInfoSchema,
} from "../../components/AccountInfo/AccountInfoForm";
import { AccountInfoPreviewSection } from "../../components/AccountInfo/AccountInfoPreviewSection";
import { ActiveTag } from "../../components/ActiveTag";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import { Table } from "../../components/Table";
import { PREVIEW_CONTACTS_COLUMNS } from "../../config/contactsTable";
import { PREVIEW_CONTRACTS_COLUMNS } from "../../config/contractsTable";
import regex from "../../config/regex";
import { ACCOUNT_DETAILS_ROUTES } from "./AccountDetails";

function FormContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="tw-flex tw-min-w-96 tw-flex-col tw-gap-8 tw-px-32">
      {children}
    </div>
  );
}

function ErrorContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="tw-text-md tw-flex tw-h-72 tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-text-primaryDim-300">
      {children}
    </div>
  );
}

function StatusContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="tw-flex tw-min-w-28 tw-items-center tw-justify-center tw-gap-2 tw-rounded-md tw-bg-neutral-50 tw-px-2.5 tw-py-1.5">
      {children}
    </div>
  );
}

function AccountInfoContainer({ children }: { children: React.ReactNode }) {
  return <div className="tw-flex tw-flex-col tw-gap-2">{children}</div>;
}

export function AccountInfo() {
  const toast = useToast();
  const history = useHistory();
  const { url } = useRouteMatch();
  const { workspace_id, accountId } = useParams<{
    workspace_id: string;
    accountId: string;
  }>();
  const isNewAccount = !Number.isInteger(Number(accountId));

  const {
    data: accountData,
    isLoading,
    error,
  } = useAccount(Number(workspace_id), Number(accountId));

  const { data: contactsData } = useContacts(Number(accountId));
  const { data: contractsData } = useAccountContracts(Number(accountId));
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const redirectTo = searchParams.get("redirectTo");
  const accountsPath = generatePath(
    `/workspaces/:workspace_id${ACCOUNT_ROUTES.base}`,
    {
      workspace_id,
    },
  );

  const { mutateAsync: createAccount } = useCreateAccount({
    workspaceId: Number(workspace_id),
  });

  const { mutateAsync: updateAccount } = useUpdateAccount({
    workspaceId: Number(workspace_id),
    accountId: Number(accountId),
  });

  const handleCreateAccount = async (newAccountData: ModifyAccountRequest) => {
    try {
      const response = await createAccount(newAccountData);

      history.replace(
        redirectTo
          ? `${redirectTo}?account_id=${response.account?.id}`
          : accountsPath,
      );
      return;
    } catch {
      toast({
        content: "Something went wrong. Could not create account.",
        variant: "error",
      });
    }
  };

  const handleUpdateAccount = async (newAccountData: ModifyAccountRequest) => {
    try {
      await updateAccount(newAccountData);
      toast({ content: "Account saved successfully!", variant: "success" });
      history.replace(accountsPath);
    } catch {
      toast({
        content: "Something went wrong. Could not save account.",
        variant: "error",
      });
    }
  };

  const handleSubmitForm = async (values: AccountInfoSchema) => {
    const newAccountData = {
      ...values,
      //API takes array of types but we only allow a single selection
      account_types: values.account_types ? [values.account_types] : undefined,
    };

    // API only accepts signed_id provided by direct upload so we remove logo from payload if its not
    if (regex.URL.test(String(newAccountData.logo)) || !newAccountData.logo) {
      delete newAccountData.logo;
    }

    if (!accountData) {
      handleCreateAccount(newAccountData);
    } else {
      handleUpdateAccount(newAccountData);
    }
  };

  if (error || isLoading) {
    return (
      <PageContent>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <ErrorContainer>
            Could not load account details
            <Button color="tertiary" onClick={history.goBack}>
              Go Back
            </Button>
          </ErrorContainer>
        )}
      </PageContent>
    );
  }

  return (
    <FormContainer>
      <div className="tw-flex tw-flex-row tw-gap-2">
        <StatusContainer>
          <Label className="tw-text-sm tw-font-semibold tw-text-neutral-500">
            Status
          </Label>
          <ActiveTag isActive={Boolean(accountData?.account?.is_active)} />
        </StatusContainer>
        <StatusContainer>
          <Label className="tw-text-sm tw-font-semibold tw-text-neutral-500">
            Parent Account
          </Label>
          {/* To be replaced with value once child accounts feature is implemented */}
          <p className="tw-text-neutral-300">N/A</p>
        </StatusContainer>
      </div>
      <AccountInfoContainer>
        <h2 className="tw-text-xl tw-font-semibold tw-text-neutral-500">
          Account Info
        </h2>
        <AccountInfoForm
          defaultValues={accountData?.account}
          onSubmit={handleSubmitForm}
        />
      </AccountInfoContainer>
      <AccountInfoPreviewSection
        title="Contacts"
        disabled={isNewAccount}
        count={contactsData?.contacts?.length}
        actions={
          <>
            <LinkButton
              to={`${url}${ACCOUNT_DETAILS_ROUTES.contacts}/new`}
              size="sm"
            >
              Add
            </LinkButton>
            <LinkButton
              to={`${url}${ACCOUNT_DETAILS_ROUTES.contacts}`}
              replace
              color="tertiary"
              size="sm"
            >
              See More
            </LinkButton>
          </>
        }
        content={
          Boolean(contactsData?.contacts?.length) && (
            <Table
              responsive
              columns={PREVIEW_CONTACTS_COLUMNS}
              rows={contactsData?.contacts?.slice(0, 4)}
            />
          )
        }
      />
      <AccountInfoPreviewSection
        title="Contracts"
        disabled={isNewAccount}
        count={contractsData?.contracts?.length}
        actions={
          <>
            <LinkButton
              to={`${url}${ACCOUNT_DETAILS_ROUTES.contracts}/new`}
              size="sm"
            >
              Add
            </LinkButton>
            <LinkButton
              to={`${url}${ACCOUNT_DETAILS_ROUTES.contracts}`}
              replace
              color="tertiary"
              size="sm"
            >
              See More
            </LinkButton>
          </>
        }
        content={
          Boolean(contractsData?.contracts?.length) && (
            <Table
              responsive
              columns={PREVIEW_CONTRACTS_COLUMNS}
              rows={contractsData?.contracts?.slice(0, 4)}
            />
          )
        }
      />
    </FormContainer>
  );
}
